import { useEffect, useMemo, useState } from 'react';

function attachmentCount(item) {
  return Array.isArray(item?.attachments) ? item.attachments.length : 0;
}

function describeItem(item) {
  const hasText = Boolean(item?.text?.trim());
  const files = attachmentCount(item);

  if (hasText && files) {
    return `${files} arquivo${files === 1 ? '' : 's'} e um texto compartilhado`;
  }
  if (files) return `${files} arquivo${files === 1 ? '' : 's'} compartilhado${files === 1 ? '' : 's'}`;
  return 'Texto compartilhado';
}

/**
 * Inbox deliberately kept outside the chat prompt.
 *
 * A share received by Android is visible here, but neither its text nor its
 * files are sent to Athena until a person explicitly takes the next action.
 * This component is reused by Home and Workspace without knowing about the
 * native bridge or private-cache attachment identifiers.
 */
export function DeviceContextTray({
  items = [],
  onUseText,
  onOpenAttachments,
  onDiscard,
  maxItems = 2,
}) {
  const [usedTextIds, setUsedTextIds] = useState(() => new Set());
  const visibleItems = useMemo(() => items.slice(0, Math.max(1, maxItems)), [items, maxItems]);
  const hiddenCount = Math.max(0, items.length - visibleItems.length);

  useEffect(() => {
    const availableIds = new Set(items.map((item) => item?.id).filter(Boolean));
    setUsedTextIds((previous) => {
      const next = new Set([...previous].filter((id) => availableIds.has(id)));
      const unchanged = next.size === previous.size && [...next].every((id) => previous.has(id));
      return unchanged ? previous : next;
    });
  }, [items]);

  if (!visibleItems.length) return null;

  const markTextUsed = (item) => {
    // The callback returns false only when it intentionally did not put the
    // text in a draft (for example, an empty shared item). This keeps the UI
    // truthful without ever exposing the text as an automatic prompt.
    const didUseText = onUseText?.(item);
    if (didUseText !== false && item?.id) {
      setUsedTextIds((previous) => new Set(previous).add(item.id));
    }
  };

  return (
    <section
      aria-label="Conteúdo recebido no dispositivo"
      style={{
        width: 'min(100%, 672px)',
        margin: '0 auto 10px',
        padding: '10px 12px',
        border: '1px solid rgba(96, 165, 250, 0.2)',
        borderRadius: 18,
        background: 'linear-gradient(135deg, rgba(12, 27, 52, 0.82), rgba(9, 13, 30, 0.86))',
        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(191, 219, 254, 0.07)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
      }}
      data-testid="device-context-tray"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#5eead4', boxShadow: '0 0 10px rgba(94,234,212,0.72)' }} />
        <span style={{ color: 'rgba(186, 230, 253, 0.9)', font: "600 10px/1 'IBM Plex Sans', system-ui, sans-serif", letterSpacing: '0.09em', textTransform: 'uppercase' }}>
          Recebido no dispositivo
        </span>
        {hiddenCount > 0 && (
          <span style={{ marginLeft: 'auto', color: 'rgba(226,232,240,0.52)', fontSize: 11 }}>
            +{hiddenCount}
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gap: 7 }}>
        {visibleItems.map((item) => {
          const hasText = Boolean(item?.text?.trim());
          const hasAttachments = attachmentCount(item) > 0;
          const textUsed = usedTextIds.has(item?.id);

          return (
            <div
              key={item?.id || describeItem(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minWidth: 0,
                padding: '8px 8px 8px 10px',
                border: '1px solid rgba(148, 163, 184, 0.13)',
                borderRadius: 12,
                background: 'rgba(3, 8, 21, 0.38)',
              }}
            >
              <span aria-hidden="true" style={{ color: '#7dd3fc', fontSize: 16, lineHeight: 1 }}>
                {hasAttachments ? '⌁' : '✦'}
              </span>
              <span style={{ minWidth: 0, flex: 1, color: 'rgba(226,232,240,0.78)', fontSize: 12, lineHeight: 1.25 }}>
                {describeItem(item)}
              </span>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
                {hasText && (
                  <button
                    type="button"
                    onClick={() => markTextUsed(item)}
                    disabled={textUsed}
                    style={{
                      minHeight: 30,
                      padding: '0 9px',
                      border: '1px solid rgba(96,165,250,0.3)',
                      borderRadius: 9,
                      color: textUsed ? 'rgba(226,232,240,0.42)' : '#bfdbfe',
                      background: textUsed ? 'rgba(30,41,59,0.3)' : 'rgba(37,99,235,0.14)',
                      font: "600 10px/1 'IBM Plex Sans', system-ui, sans-serif",
                      cursor: textUsed ? 'default' : 'pointer',
                    }}
                    data-testid={`use-shared-text-${item?.id || 'item'}`}
                  >
                    {textUsed ? 'No rascunho' : 'Usar texto'}
                  </button>
                )}
                {hasAttachments && typeof onOpenAttachments === 'function' && (
                  <button
                    type="button"
                    onClick={() => onOpenAttachments(item)}
                    style={{
                      minHeight: 30,
                      padding: '0 9px',
                      border: '1px solid rgba(45, 212, 191, 0.28)',
                      borderRadius: 9,
                      color: '#99f6e4',
                      background: 'rgba(13, 148, 136, 0.12)',
                      font: "600 10px/1 'IBM Plex Sans', system-ui, sans-serif",
                      cursor: 'pointer',
                    }}
                    data-testid={`open-shared-files-${item?.id || 'item'}`}
                  >
                    Workspace
                  </button>
                )}
                {typeof onDiscard === 'function' && (
                  <button
                    type="button"
                    onClick={() => onDiscard(item)}
                    aria-label={`Descartar ${describeItem(item)}`}
                    title="Descartar conteúdo recebido"
                    style={{
                      width: 30,
                      height: 30,
                      padding: 0,
                      border: '1px solid rgba(248,113,113,0.18)',
                      borderRadius: 9,
                      color: 'rgba(254,202,202,0.74)',
                      background: 'rgba(127,29,29,0.1)',
                      fontSize: 16,
                      lineHeight: 1,
                      cursor: 'pointer',
                    }}
                    data-testid={`discard-shared-context-${item?.id || 'item'}`}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default DeviceContextTray;
